import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, List, Plus, Search, Settings } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useRowFx, undoToast, fillStyle } from "@/lib/orderStatus";
import { Switch } from "@/components/ui/switch";
import { OrderDialog } from "./counter/OrderDialog";
import { SettingsDialog } from "./counter/SettingsDialog";
import { OrderCard, ROW_GRID } from "./counter/OrderCard";
import { LateAlert, StatusCards, MonthNav, MonthCalendar } from "./counter/Panels";
import { todayIso, longDate, daysSince, iso, STATUS, STATUS_KEYS } from "./counter/utils";
import { WorkOrderDialog } from "./counter/WorkOrderDialog";
import { WorkOrderCard } from "./counter/WorkOrderCard";
import { WoSettingsDialog } from "./counter/WoSettingsDialog";
import { WO_KEYS, WO_STATUS } from "./counter/woUtils";

const DONE = { counter: "completato", wo: "ritirato" };
const byFlow = (flow, dateOf) => (a, b) => flow.indexOf(a.status) - flow.indexOf(b.status) || String(dateOf(a)).localeCompare(String(dateOf(b)));
const localDay = (s) => (s ? iso(new Date(s)) : "");
const COLS = ["Stato", "N° ordine", "Data", "Cliente", "Prodotto / descrizione", "Totale", "Azioni"];
const WO_COLS = ["Stato", "N° ordine", "Consegna", "Cliente", "Prodotto / descrizione", "Totale", "Azioni"];

function Box({ title, count, children, empty, testid, row, cols, tone = "bg-slate-50" }) {
  return (
    <div className="rounded-xl border-2 border-slate-200 bg-white overflow-hidden" data-testid={testid}>
      <div className={`px-4 py-3 flex items-center justify-between border-b ${tone}`}>
        <h3 className="text-lg font-extrabold uppercase first-letter:uppercase">{title}</h3>
        <span className="font-mono text-sm text-slate-700" data-testid={`${testid}-count`}>{count} {count === 1 ? "ordine" : "ordini"}</span>
      </div>
      {row && count > 0 && <div className={`hidden xl:grid ${ROW_GRID} pl-[22px] pr-4 py-2 bg-slate-100 border-b font-mono text-xs uppercase text-slate-700`}>{cols.map((c) => <div key={c}>{c}</div>)}</div>}
      <div>{children}</div>
      {!count && <p className="p-5 text-base text-slate-700">{empty}</p>}
    </div>
  );
}

export function AdminCounterOrders() {
  const [orders, setOrders] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [filter, setFilter] = useState("");
  const [view, setView] = useState("calendar");
  const [q, setQ] = useState("");
  const [cur, setCur] = useState(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), 1); });
  const [day, setDay] = useState(todayIso());
  const [edit, setEdit] = useState(null);
  const [showCfg, setShowCfg] = useState(false);
  const [wos, setWos] = useState([]);
  const [woCfg, setWoCfg] = useState(null);
  const [woEdit, setWoEdit] = useState(null);
  const [woCfgOpen, setWoCfgOpen] = useState(false);
  const [woFilter, setWoFilter] = useState("");
  const [showDone, setShowDone] = useState(false);
  const fx = useRowFx();
  const loadWo = () => api.get("/admin/work-orders").then((r) => {
    const l = r.data.map((o) => ({ ...o, kind: "wo" }));
    setWos(l);
    setWoEdit((p) => (p?.id ? l.find((x) => x.id === p.id) || p : p));
  }).catch((e) => toast.error(errMsg(e)));
  const load = () => { loadWo(); return api.get("/admin/counter-orders").then((r) => setOrders(r.data)).catch((e) => toast.error(errMsg(e))); };
  useEffect(() => { load(); api.get("/admin/counter-orders/settings").then((r) => setCfg(r.data)); api.get("/admin/work-orders/settings").then((r) => setWoCfg(r.data)); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showWo = (o) => showDone || woFilter === "ritirato" || o.status !== "ritirato";
  const showCo = (o) => showDone || o.status !== "completato";
  const woMatch = useMemo(() => {
    const t = q.trim().toLowerCase();
    return wos.filter((o) => (!woFilter || o.status === woFilter) && (!t || `${o.number} ${o.client.name} ${o.client.phone} ${o.client.email} ${o.product_type} ${o.description} ${o.notes}`.toLowerCase().includes(t)));
  }, [wos, woFilter, q]);
  const woVisible = useMemo(() => woMatch.filter(showWo).sort(byFlow(WO_KEYS, (o) => o.created_at)), [woMatch, showDone]); // eslint-disable-line react-hooks/exhaustive-deps
  const matching = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (orders || []).filter((o) => (!filter || o.status === filter) && (!t || `${o.name} ${o.item} ${o.code || ""} ${o.booking_number || ""} ${o.phone} ${o.email || ""} ${o.notes}`.toLowerCase().includes(t)));
  }, [orders, filter, q]);
  const visible = useMemo(() => matching.filter(showCo).sort(byFlow(STATUS_KEYS, (o) => `${o.date}${o.created_at || ""}`)), [matching, showDone]); // eslint-disable-line react-hooks/exhaustive-deps
  const byDay = useMemo(() => [...woMatch.filter((o) => o.status !== "ritirato").map((o) => ({ ...o, date: o.due_date })), ...matching]
    .reduce((a, o) => ({ ...a, [o.date]: [...(a[o.date] || []), o] }), {}), [matching, woMatch]);
  const counts = useMemo(() => (orders || []).reduce((a, o) => ({ ...a, [o.status]: (a[o.status] || 0) + 1 }), {}), [orders]);
  const doneToday = useMemo(() => (orders || []).filter((o) => o.status === "completato" && localDay(o.updated_at) === todayIso()).length, [orders]);
  const late = useMemo(() => (orders || []).filter((o) => o.status === "sospeso" && cfg && daysSince(o.date) > cfg.threshold_days).sort((a, b) => a.date.localeCompare(b.date)), [orders, cfg]);

  const move = async (kind, o, status, undo) => {
    const set = kind === "wo" ? setWos : setOrders;
    const url = kind === "wo" ? `/admin/work-orders/${o.id}/status` : `/admin/counter-orders/${o.id}/status`;
    const prev = o.status;
    const apply = (st) => set((l) => l.map((x) => (x.id === o.id ? { ...x, status: st, updated_at: new Date().toISOString() } : x)));
    if (status === DONE[kind] && !showDone) fx.leave(o.id, () => apply(status)); else { apply(status); fx.doFlash(o.id); }
    try { await api.patch(url, { status }); } catch (e) { toast.error(errMsg(e)); apply(prev); return; }
    if (status === DONE[kind] && !undo) undoToast(`Ordine ${kind === "wo" ? o.number : `di ${o.name}`} completato`, () => move(kind, { ...o, status }, prev, true));
  };
  const setStatus = (o, status) => move("counter", o, status);
  const del = async (o) => {
    try { await api.delete(`/admin/counter-orders/${o.id}`); toast.success("Ordine eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const actions = { onStatus: setStatus, onEdit: setEdit, onDelete: del, onNotified: load };
  const woActions = {
    onEdit: setWoEdit, onChanged: loadWo, onStatus: (o, status) => move("wo", o, status),
    onDelete: async (o) => { try { await api.delete(`/admin/work-orders/${o.id}`); toast.success("Ordine eliminato"); loadWo(); } catch (e) { toast.error(errMsg(e)); } },
  };
  if (!orders || !cfg) return <p className="text-sm text-slate-500">Caricamento…</p>;
  const dayAll = byDay[day] || [];
  const dayList = dayAll.filter((o) => o.kind !== "wo" && showCo(o)).sort(byFlow(STATUS_KEYS, (o) => o.created_at || ""));
  const dayWo = dayAll.filter((o) => o.kind === "wo");
  const coCard = (o, row) => <OrderCard key={o.id} o={o} cfg={cfg} row={row} fx={fx.cls(o.id)} done={o.status === "completato"} {...actions} />;
  const woCard = (o, row) => <WorkOrderCard key={o.id} o={o} row={row} fx={fx.cls(o.id)} done={o.status === "ritirato"} {...woActions} />;

  return (
    <div className="space-y-6" data-testid="admin-counter-orders">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[11px] tracking-widest text-slate-500">GESTIONE NEGOZIO</div>
          <h1 className="text-4xl font-extrabold uppercase">Ordini al banco</h1>
          <p className="text-sm text-slate-500">Ordini presi in negozio: da ordinare al fornitore, da consegnare, ritirati.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="counter-settings-btn" onClick={() => setShowCfg(true)} className="press h-11 w-11 grid place-items-center rounded-md border bg-white" title="Impostazioni"><Settings className="w-5 h-5" /></button>
          <button data-testid="wo-settings-btn" onClick={() => setWoCfgOpen(true)} className="press h-11 px-3 rounded-md border bg-white text-sm font-semibold" title="Impostazioni ordini in lavorazione">Impost. lavorazioni</button>
          <button data-testid="wo-new-btn" onClick={() => setWoEdit({ due_date: day >= todayIso() ? day : todayIso() })} className="press h-11 px-5 rounded-md bg-violet-700 text-white font-bold flex items-center gap-2"><Plus className="w-5 h-5" /> Ordine in lavorazione</button>
          <button data-testid="counter-new-btn" onClick={() => setEdit({ date: day || todayIso() })} className="press h-11 px-5 rounded-md bg-brand-yellow text-brand-navy font-bold flex items-center gap-2"><Plus className="w-5 h-5" /> Nuovo ordine</button>
        </div>
      </div>

      <LateAlert late={late} pending={counts.sospeso || 0} cfg={cfg} onMark={setStatus} />
      <StatusCards counts={counts} filter={filter} setFilter={setFilter} doneToday={doneToday} showDone={showDone} setShowDone={setShowDone} />
      <div className="flex flex-wrap items-center gap-2" data-testid="wo-status-filter">
        <span className="text-sm font-mono uppercase text-slate-700">Lavorazioni:</span>
        {WO_KEYS.filter((k) => k !== "ritirato").map((k) => { const c = wos.filter((o) => o.status === k).length; return (
          <button key={k} data-testid={`wo-filter-${k}`} onClick={() => { setWoFilter(woFilter === k ? "" : k); setView("list"); }} style={fillStyle(k)} className={`press h-9 px-3 rounded-full text-sm font-bold ${woFilter === k ? "ring-4 ring-offset-1 ring-brand-navy" : ""}`}>{WO_STATUS[k].label} {c}</button>); })}
        <label className="ml-auto flex items-center gap-2 text-sm font-semibold cursor-pointer select-none">
          <Switch data-testid="counter-show-done" checked={showDone} onCheckedChange={setShowDone} /> Mostra completati
        </label>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {view === "calendar" ? <MonthNav cur={cur} setCur={setCur} setDay={setDay} /> : <h2 className="text-xl font-extrabold uppercase">Elenco ordini</h2>}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="counter-search-input" value={q} onChange={(e) => { setQ(e.target.value); if (e.target.value) setView("list"); }} placeholder="Cerca cliente, articolo, numero"
              className="h-10 pl-9 pr-3 w-64 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow/70" />
          </div>
          <div className="inline-flex rounded-md border bg-white p-0.5">
            {[["calendar", "Calendario", CalendarDays], ["list", "Elenco", List]].map(([k, l, I]) => (
              <button key={k} data-testid={`counter-view-${k}`} onClick={() => setView(k)} className={`press h-9 px-3 rounded text-sm font-semibold flex items-center gap-1.5 ${view === k ? "bg-brand-navy text-white" : ""}`}><I className="w-4 h-4" /> {l}</button>
            ))}
          </div>
        </div>
      </div>

      {view === "calendar" ? (
        <div className="grid xl:grid-cols-[1.5fr_1fr] gap-4 items-start">
          <MonthCalendar cur={cur} byDay={byDay} day={day} setDay={setDay} onOpen={setWoEdit} />
          <div className="xl:sticky xl:top-[110px] space-y-4">
            {dayWo.length > 0 && <Box testid="wo-day-panel" tone="bg-violet-50" title={`In consegna · ${longDate(day)}`} count={dayWo.length} empty="">{dayWo.map((o) => woCard(o, false))}</Box>}
            <Box testid="counter-day-panel" title={longDate(day)} count={dayList.length}
              empty={<>Nessun ordine attivo in questo giorno. <button className="underline font-semibold text-brand-navy" data-testid="counter-day-add-btn" onClick={() => setEdit({ date: day })}>Aggiungine uno</button></>}>{dayList.map((o) => coCard(o, false))}</Box>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <Box testid="counter-list" row cols={COLS} title={filter ? `Ordini al banco · ${STATUS[filter]?.label || filter}` : "Ordini al banco"} count={visible.length} empty={showDone ? "Nessun ordine trovato." : "Nessun ordine attivo."}>{visible.map((o) => coCard(o, true))}</Box>
          <Box testid="wo-list" row cols={WO_COLS} tone="bg-violet-50" title={woFilter ? `Lavorazioni · ${WO_STATUS[woFilter].label}` : "Ordini in lavorazione"} count={woVisible.length} empty="Nessun ordine in lavorazione attivo.">{woVisible.map((o) => woCard(o, true))}</Box>
        </div>
      )}

      <OrderDialog order={edit} onClose={() => setEdit(null)} onSaved={(o) => { setEdit(null); setDay(o.date); const d = new Date(`${o.date}T12:00:00`); setCur(new Date(d.getFullYear(), d.getMonth(), 1)); load(); }} />
      <WorkOrderDialog order={woEdit} types={woCfg?.types || []} onClose={() => setWoEdit(null)}
        onSaved={(o) => { if (!o) return loadWo(); setWoEdit(null); setDay(o.due_date); const d = new Date(`${o.due_date}T12:00:00`); setCur(new Date(d.getFullYear(), d.getMonth(), 1)); loadWo(); }} />
      <WoSettingsDialog open={woCfgOpen} cfg={woCfg} onClose={() => setWoCfgOpen(false)} onSaved={(c) => { setWoCfg(c); setWoCfgOpen(false); }} />
      <SettingsDialog open={showCfg} cfg={cfg} onClose={() => setShowCfg(false)} onSaved={(c) => { setCfg(c); setShowCfg(false); }} />
    </div>
  );
}
